/**
 * The Coveo Headless SSR Commerce sub-package exposes exposes the engine, definers, controllers, actions, and utility functions to build a server side rendered commerce experience.
 *
 * @example
 * ```typescript
 * import {
 *   Controller,
 *   ControllerDefinitionsMap,
 *   CommerceEngineDefinitionOptions,
 *   CommerceEngine,
 *   defineCommerceEngine,
 *   InferStaticState,
 *   InferHydratedState,
 *   defineProductList,
 *   getSampleCommerceEngineConfiguration,
 * } from '@coveo/headless/ssr-commerce';
 *
 * type CommerceEngineConfig = CommerceEngineDefinitionOptions<
 *   ControllerDefinitionsMap<CommerceEngine, Controller>
 * >;
 *
 * const config = {
 *   configuration: {
 *     ...getSampleCommerceEngineConfiguration(),
 *     context: {
 *       language: 'en',
 *       country: 'US',
 *       currency: 'USD',
 *       view: {
 *         url: '...',
 *       },
 *     },
 *   },
 *   controllers: {
 *     productList: defineProductList(),
 *   },
 * } satisfies CommerceEngineConfig;
 *
 * const engineDefinition = defineCommerceEngine(engineConfig);
 *
 * export const {
 *   listingEngineDefinition,
 *   searchEngineDefinition,
 *   standaloneEngineDefinition,
 * } = engineDefinition;
 *
 * export type ListingStaticState = InferStaticState<
 *   typeof listingEngineDefinition
 * >;
 * export type ListingHydratedState = InferHydratedState<
 *   typeof listingEngineDefinition
 * >;
 *
 * export type SearchStaticState = InferStaticState<typeof searchEngineDefinition>;
 * export type SearchHydratedState = InferHydratedState<
 *   typeof searchEngineDefinition
 * >;
 *
 * export type StandaloneStaticState = InferStaticState<
 *   typeof standaloneEngineDefinition
 * >;
 * export type StandaloneHydratedState = InferHydratedState<
 *   typeof standaloneEngineDefinition
 * >;
 *
 * ```
 * @module SSR Commerce
 */
export { getSampleCommerceEngineConfiguration } from './app/commerce-engine/commerce-engine-configuration.js';
export { defineBreadcrumbManager } from './ssr/commerce/controllers/breadcrumb-manager/headless-core-breadcrumb-manager.ssr.js';
export { defineCart } from './ssr/commerce/controllers/cart/headless-cart.ssr.js';
export { defineContext } from './ssr/commerce/controllers/context/headless-context.ssr.js';
export { defineDidYouMean } from './ssr/commerce/controllers/did-you-mean/headless-did-you-mean.ssr.js';
export { defineFilterSuggestionsGenerator } from './ssr/commerce/controllers/filter-suggestions/headless-filter-suggestions-generator.ssr.js';
export { defineFacetGenerator } from './ssr/commerce/controllers/generator/headless-commerce-facet-generator.ssr.js';
export { defineInstantProducts } from './ssr/commerce/controllers/instant-products/headless-instant-products.ssr.js';
export { definePagination } from './ssr/commerce/controllers/pagination/headless-core-commerce-pagination.ssr.js';
export { defineParameterManager } from './ssr/commerce/controllers/parameter-manager/headless-core-parameter-manager.ssr.js';
export { defineProductList } from './ssr/commerce/controllers/product-list/headless-product-list.ssr.js';
export { defineProductView } from './ssr/commerce/controllers/product-view/headless-product-view.ssr.js';
export { defineRecentQueriesList } from './ssr/commerce/controllers/recent-queries-list/headless-recent-queries-list.ssr.js';
export { defineRecommendations } from './ssr/commerce/controllers/recommendations/headless-recommendations.ssr.js';
export { defineSearchBox } from './ssr/commerce/controllers/search-box/headless-search-box.ssr.js';
export { defineSort } from './ssr/commerce/controllers/sort/headless-core-commerce-sort.ssr.js';
export { defineStandaloneSearchBox } from './ssr/commerce/controllers/standalone-search-box/headless-standalone-search-box.ssr.js';
export { defineSummary } from './ssr/commerce/controllers/summary/headless-core-summary.ssr.js';
export { defineNotifyTrigger } from './ssr/commerce/controllers/triggers/headless-notify-trigger.ssr.js';
export { defineQueryTrigger } from './ssr/commerce/controllers/triggers/headless-query-trigger.ssr.js';
export { defineRedirectionTrigger } from './ssr/commerce/controllers/triggers/headless-redirection-trigger.ssr.js';
export { defineCommerceEngine } from './ssr/commerce/engine/commerce-engine.ssr.js';
export { SolutionType } from './ssr/commerce/types/controller-constants.js';
export { Kind } from './ssr/commerce/types/kind.js';
// TODO: KIT-3391 - export other SSR commerce controllers
//#endregion
export * from './features/commerce/configuration/configuration-actions-loader.js';
export * from './features/commerce/context/cart/cart-actions-loader.js';
//#region Grouped actions
export * from './features/commerce/context/context-actions-loader.js';
export * from './features/commerce/facets/category-facet/category-facet-actions-loader.js';
export * from './features/commerce/facets/core-facet/core-facet-actions-loader.js';
export * from './features/commerce/facets/date-facet/date-facet-actions-loader.js';
export * from './features/commerce/facets/numeric-facet/numeric-facet-actions-loader.js';
export * from './features/commerce/facets/regular-facet/regular-facet-actions-loader.js';
export * from './features/commerce/instant-products/instant-products-actions-loader.js';
export * from './features/commerce/pagination/pagination-actions-loader.js';
export * from './features/commerce/product/product-actions-loaders.js';
export * from './features/commerce/product-listing/product-listing-actions-loader.js';
export * from './features/commerce/product-listing-parameters/product-listing-parameters-actions-loader.js';
export * from './features/commerce/query/query-actions-loader.js';
export * from './features/commerce/query-set/query-set-actions-loader.js';
export * from './features/commerce/query-suggest/query-suggest-actions-loader.js';
export * from './features/commerce/recent-queries/recent-queries-actions-loader.js';
export * from './features/commerce/recommendations/recommendations-actions-loader.js';
export * from './features/commerce/search/search-actions-loader.js';
export * from './features/commerce/search-parameters/search-parameters-actions-loader.js';
export * from './features/commerce/sort/sort-actions-loader.js';
export * from './features/commerce/standalone-search-box-set/standalone-search-box-set-actions-loader.js';
export * from './features/commerce/triggers/triggers-actions-loader.js';
export { buildResultTemplatesManager } from './features/result-templates/result-templates-manager.js';
//#endregion
export { getCommerceApiBaseUrl } from './api/commerce/commerce-api-client.js';
export { getAnalyticsNextApiBaseUrl, getOrganizationEndpoint, } from './api/platform-client.js';
export { deserializeRelativeDate, validateRelativeDate, } from './api/search/date/relative-date.js';
export { buildParameterSerializer } from './features/commerce/parameters/parameters-serializer.ssr.js';
export { ProductTemplatesHelpers } from './features/commerce/product-templates/product-templates-helpers.js';
export { SortBy } from './features/commerce/sort/sort.js';
export { buildCriterionExpression, buildDateSortCriterion, buildFieldSortCriterion, buildNoSortCriterion, buildQueryRankingExpressionSortCriterion, buildRelevanceSortCriterion, SortOrder, } from './features/sort-criteria/criteria.js';
export { parseCriterionExpression } from './features/sort-criteria/criteria-parser.js';
export * from './utils/query-expression/query-expression.js';
export { VERSION } from './utils/version.js';
